import React from 'react';
import { motion } from 'framer-motion';
import { 
  Shield, Users, FileText, FolderOpen, Settings, 
  Check, X, AlertCircle, Info, Download, Plus
} from 'lucide-react';

const StyleGuide = () => {
  return (
    <div className="p-8 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-4xl font-bold mb-2">Style Guide</h1>
        <p className="text-gray-600 mb-8">All UI components and styles in one place</p>

        {/* Colors */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-6">Colors</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <div className="text-center">
              <div className="w-full h-24 bg-primary-600 rounded-lg mb-2"></div>
              <p className="text-sm font-medium">Primary</p>
              <p className="text-xs text-gray-500">#0ea5e9</p>
            </div>
            <div className="text-center">
              <div className="w-full h-24 bg-green-600 rounded-lg mb-2"></div>
              <p className="text-sm font-medium">Success</p>
              <p className="text-xs text-gray-500">#10b981</p>
            </div>
            <div className="text-center">
              <div className="w-full h-24 bg-red-600 rounded-lg mb-2"></div>
              <p className="text-sm font-medium">Danger</p>
              <p className="text-xs text-gray-500">#ef4444</p>
            </div>
            <div className="text-center">
              <div className="w-full h-24 bg-yellow-600 rounded-lg mb-2"></div>
              <p className="text-sm font-medium">Warning</p>
              <p className="text-xs text-gray-500">#f59e0b</p>
            </div>
            <div className="text-center">
              <div className="w-full h-24 bg-blue-600 rounded-lg mb-2"></div>
              <p className="text-sm font-medium">Info</p>
              <p className="text-xs text-gray-500">#3b82f6</p>
            </div>
            <div className="text-center">
              <div className="w-full h-24 bg-gray-600 rounded-lg mb-2"></div>
              <p className="text-sm font-medium">Secondary</p>
              <p className="text-xs text-gray-500">#64748b</p>
            </div>
          </div>
        </section>

        {/* Typography */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-6">Typography</h2>
          <div className="card card-body space-y-4">
            <h1>Heading 1 - The quick brown fox</h1>
            <h2>Heading 2 - The quick brown fox</h2>
            <h3>Heading 3 - The quick brown fox</h3>
            <h4>Heading 4 - The quick brown fox</h4>
            <h5>Heading 5 - The quick brown fox</h5>
            <h6>Heading 6 - The quick brown fox</h6>
            <p>Regular paragraph text. Lorem ipsum dolor sit amet, consectetur adipiscing elit.</p>
            <p className="text-sm text-gray-600">Small text with secondary color</p>
            <p className="text-gradient text-2xl font-bold">Gradient Text Effect</p>
          </div>
        </section>

        {/* Buttons */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-6">Buttons</h2>
          <div className="card card-body">
            <div className="space-y-4">
              <div className="flex flex-wrap gap-3">
                <button className="btn btn-primary">Primary Button</button>
                <button className="btn btn-secondary">Secondary</button>
                <button className="btn btn-success">Success</button>
                <button className="btn btn-danger">Danger</button>
                <button className="btn btn-outline">Outline</button>
                <button className="btn btn-ghost">Ghost</button>
              </div>
              <div className="flex flex-wrap gap-3">
                <button className="btn btn-primary btn-lg">Large Button</button>
                <button className="btn btn-primary">Normal Button</button>
                <button className="btn btn-primary btn-sm">Small Button</button>
              </div>
              <div className="flex flex-wrap gap-3">
                <button className="btn btn-primary" disabled>Disabled</button>
                <button className="btn btn-primary">
                  <Plus className="w-4 h-4 mr-2" />
                  With Icon
                </button>
                <button className="btn btn-primary">
                  <span className="spinner spinner-sm mr-2"></span>
                  Loading
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Cards */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-6">Cards</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <motion.div className="card hover-lift">
              <div className="card-header">
                <h3 className="text-lg font-semibold">Card Header</h3>
              </div>
              <div className="card-body">
                <p>Card content goes here. This card has hover lift effect.</p>
              </div>
              <div className="card-footer">
                <button className="btn btn-primary btn-sm">Action</button>
              </div>
            </motion.div>

            <div className="card glass">
              <div className="card-body">
                <h3 className="text-lg font-semibold mb-2">Glass Card</h3>
                <p>This card uses glass morphism effect.</p>
              </div>
            </div>

            <div className="card gradient-primary text-white">
              <div className="card-body">
                <h3 className="text-lg font-semibold mb-2">Gradient Card</h3>
                <p>This card has a gradient background.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Forms */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-6">Forms</h2>
          <div className="card card-body">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="form-group">
                <label className="form-label">Input Field</label>
                <input type="text" className="w-full border border-gray-300 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary-500 py-1.5 rounded-lg px-3 input-field text-sm" placeholder="Enter text..." />
              </div>

              <div className="form-group">
                <label className="form-label">With Icon</label>
                <div className="input-group">
                  <Users className="input-icon" />
                  <input type="text" className="w-full border border-gray-300 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary-500 py-1.5 rounded-lg px-3 input-field text-sm" placeholder="Username" />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Select</label>
                <select className="select">
                  <option>Option 1</option>
                  <option>Option 2</option>
                  <option>Option 3</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Error State</label>
                <input type="text" className="w-full border border-gray-300 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary-500 py-1.5 rounded-lg px-3 input-field error text-sm" placeholder="Error input" />
                <p className="text-red-500 text-sm mt-1">This field has an error</p>
              </div>

              <div className="form-group md:col-span-2">
                <label className="form-label">Textarea</label>
                <textarea className="w-full border border-gray-300 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary-500 py-1.5 rounded-lg px-3 textarea text-sm" rows="3" placeholder="Enter message..."></textarea>
              </div>

              <div className="space-y-3">
                <label className="flex items-center">
                  <input type="checkbox" className="checkbox mr-2" />
                  <span>Checkbox option</span>
                </label>
                <label className="flex items-center">
                  <input type="radio" className="radio mr-2" name="radio" />
                  <span>Radio option 1</span>
                </label>
                <label className="flex items-center">
                  <input type="radio" className="radio mr-2" name="radio" />
                  <span>Radio option 2</span>
                </label>
              </div>
            </div>
          </div>
        </section>

        {/* Badges & Alerts */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-6">Badges & Alerts</h2>
          <div className="card card-body space-y-6">
            <div>
              <h3 className="font-semibold mb-3">Badges</h3>
              <div className="flex flex-wrap gap-2">
                <span className="badge badge-primary">Primary</span>
                <span className="badge badge-success">Success</span>
                <span className="badge badge-danger">Danger</span>
                <span className="badge badge-warning">Warning</span>
                <span className="badge badge-info">Info</span>
              </div>
            </div>

            <div>
              <h3 className="font-semibold mb-3">Alerts</h3>
              <div className="space-y-3">
                <div className="alert alert-success">
                  <Check className="w-5 h-5 mr-2 inline" />
                  Success alert message
                </div>
                <div className="alert alert-danger">
                  <X className="w-5 h-5 mr-2 inline" />
                  Error alert message
                </div>
                <div className="alert alert-warning">
                  <AlertCircle className="w-5 h-5 mr-2 inline" />
                  Warning alert message
                </div>
                <div className="alert alert-info">
                  <Info className="w-5 h-5 mr-2 inline" />
                  Info alert message
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Loading States */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-6">Loading States</h2>
          <div className="card card-body">
            <div className="space-y-6">
              <div>
                <h3 className="font-semibold mb-3">Spinners</h3>
                <div className="flex items-center gap-4">
                  <span className="spinner spinner-sm border-primary-600"></span>
                  <span className="spinner spinner-md border-primary-600"></span>
                  <span className="spinner spinner-lg border-primary-600"></span>
                </div>
              </div>

              <div>
                <h3 className="font-semibold mb-3">Skeleton Loading</h3>
                <div className="space-y-3">
                  <div className="skeleton skeleton-title"></div>
                  <div className="skeleton skeleton-text"></div>
                  <div className="skeleton skeleton-text"></div>
                  <div className="flex gap-3">
                    <div className="skeleton skeleton-avatar"></div>
                    <div className="flex-1 space-y-2">
                      <div className="skeleton skeleton-text"></div>
                      <div className="skeleton skeleton-text w-3/4"></div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-semibold mb-3">Progress Bar</h3>
                <div className="space-y-3">
                  <div className="progress">
                    <div className="progress-bar" style={{ width: '25%' }}></div>
                  </div>
                  <div className="progress">
                    <div className="progress-bar bg-success-600" style={{ width: '50%' }}></div>
                  </div>
                  <div className="progress">
                    <div className="progress-bar bg-danger-600" style={{ width: '75%' }}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Tables */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-6">Tables</h2>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>John Doe</td>
                  <td>john@example.com</td>
                  <td><span className="badge badge-success">Active</span></td>
                  <td>
                    <button className="btn btn-sm btn-ghost">Edit</button>
                  </td>
                </tr>
                <tr>
                  <td>Jane Smith</td>
                  <td>jane@example.com</td>
                  <td><span className="badge badge-warning">Pending</span></td>
                  <td>
                    <button className="btn btn-sm btn-ghost">Edit</button>
                  </td>
                </tr>
                <tr>
                  <td>Bob Johnson</td>
                  <td>bob@example.com</td>
                  <td><span className="badge badge-danger">Inactive</span></td>
                  <td>
                    <button className="btn btn-sm btn-ghost">Edit</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Animations */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-6">Animations</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="card card-body text-center"
            >
              <h3 className="font-semibold mb-2">Fade In</h3>
              <p>This card fades in on load</p>
            </motion.div>

            <div className="card card-body text-center hover-scale">
              <h3 className="font-semibold mb-2">Hover Scale</h3>
              <p>Hover to see scale effect</p>
            </div>

            <div className="relative overflow-hidden rounded-lg p-8 bg-gradient-primary">
              <div className="absolute -top-10 -right-10 w-32 h-32 bg-white/20 rounded-full animate-blob"></div>
              <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-white/20 rounded-full animate-blob animation-delay-2000"></div>
              <h3 className="text-white font-semibold mb-2">Blob Animation</h3>
              <p className="text-white/90">Animated background blobs</p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default StyleGuide;